// The trainings area: the editions calendar above the trainings table. Each
// training and each edition then has its own page: /admin/trainings/[id] and
// /admin/editions/[#].

import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	redirect,
	useRouteContext,
} from "@tanstack/react-router";
import {
	ChevronLeft,
	Loader2,
	Pencil,
	Plus,
	Search,
	Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
	EditionDialog,
	type EditionRow,
	EditionStatusBadge,
	FILTER_SELECT,
	ROW_ACTION,
} from "#/components/admin/editions";
import { EditionsCalendar } from "#/components/admin/editions-calendar";
import { GhostButton, PrimaryButton } from "#/components/admin/modal";
import { TabBar } from "#/components/admin/tabs";
import {
	TrainingDialogLoader,
	useTrainingRemoval,
} from "#/components/admin/training-dialog";
import {
	asTemplateKey,
	TEMPLATE_KEYS,
	TEMPLATE_LABELS,
	type TemplateKey,
} from "#/components/templates/registry";
import { authClient } from "#/lib/auth-client";
import {
	EDITION_STATUS_LABELS,
	type EditionStatus,
	editionLabel,
	editionStatus,
	daysLeftLabel,
	isoDay,
	nextSessionOf,
	pickCurrentEdition,
} from "#/lib/editions";
import { algiersToday } from "#/lib/lead-filters";
import {
	canManageEditions,
	canManageTrainings,
	canViewEditions,
	canViewTrainings,
	homeRouteFor,
} from "#/lib/roles";
import { getSession } from "#/lib/session";

export const Route = createFileRoute("/admin/trainings")({
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		// Every role but the designer may open the area; what each one sees
		// inside is decided below.
		if (!canViewEditions(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: TrainingsPage,
});

interface TrainingSummary {
	id: string;
	slug: string;
	name: string;
	isDefault: boolean;
	isActive: boolean;
	template: string;
}

function TrainingsPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	const { data: session } = authClient.useSession();
	const role = session?.user?.role;
	// The community manager sees the calendar, not the trainings table — the
	// same schedule view the scheduling page gave them.
	const showTrainings = canViewTrainings(role);
	const today = algiersToday();

	const [trainingDialog, setTrainingDialog] = useState<string | "new" | null>(
		null,
	);
	const [addingEdition, setAddingEdition] = useState(false);

	const trainingsQuery = useQuery(trpc.trainings.list.queryOptions());
	const editionsQuery = useQuery(trpc.editions.list.queryOptions());
	const trainings = trainingsQuery.data ?? [];
	const editions = (editionsQuery.data ?? []) as EditionRow[];
	const isLoading = trainingsQuery.isLoading || editionsQuery.isLoading;

	return (
		<div className="flex flex-col gap-5">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-navy">التدريبات</h1>
					<p className="text-sm text-muted">
						{showTrainings
							? "تقويم الطبعات وكل تدريب بصفحة هبوطه — افتح تدريباً لإدارة طبعاته."
							: "تقويم الطبعات المبرمجة."}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{canManageEditions(role) ? (
						<GhostButton onClick={() => setAddingEdition(true)}>
							<Plus className="h-4 w-4" />
							طبعة جديدة
						</GhostButton>
					) : null}
					{canManageTrainings(role) ? (
						<PrimaryButton onClick={() => setTrainingDialog("new")}>
							<Plus className="h-4 w-4" />
							تدريب جديد
						</PrimaryButton>
					) : null}
				</div>
			</header>

			{isLoading ? (
				<div className="flex justify-center rounded-2xl border border-line bg-white py-16 shadow-card">
					<Loader2 className="h-5 w-5 animate-spin text-muted" />
				</div>
			) : (
				<EditionsCalendar
					editions={editions}
					trainings={trainings}
					today={today}
				/>
			)}
			{showTrainings ? (
				<TrainingsTable
					trainings={trainings}
					editions={editions}
					today={today}
					isLoading={isLoading}
					canEdit={canManageTrainings(role)}
					onEdit={setTrainingDialog}
				/>
			) : null}

			{trainingDialog ? (
				<TrainingDialogLoader
					trainingId={trainingDialog === "new" ? null : trainingDialog}
					onClose={() => setTrainingDialog(null)}
				/>
			) : null}

			{addingEdition ? (
				<EditionDialog edition={null} onClose={() => setAddingEdition(false)} />
			) : null}
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Filters                                                                   */
/* -------------------------------------------------------------------------- */

interface TrainingFilterValue {
	/** Matched against the name and the /training/<slug> link. */
	query: string;
	/** Where the training's current run stands — see pickCurrentEdition. */
	edition: "all" | EditionStatus | "none";
	template: "all" | TemplateKey;
}

const NO_FILTERS: TrainingFilterValue = {
	query: "",
	edition: "all",
	template: "all",
};

type TrainingTab = "active" | "inactive";

/** A session this many days away or fewer is highlighted in the table. */
const SOON_DAYS = 7;

const EDITION_FILTERS = [
	"all",
	"running",
	"upcoming",
	"finished",
	"none",
] as const satisfies readonly TrainingFilterValue["edition"][];

/** Lower-cased, with Arabic letter variants folded, so «انشاء» finds «إنشاء». */
function normalize(text: string) {
	return text
		.toLowerCase()
		.replace(/[إأآا]/g, "ا")
		.replace(/ى/g, "ي")
		.replace(/ة/g, "ه")
		.trim();
}

function TrainingFilters({
	value,
	onChange,
}: {
	value: TrainingFilterValue;
	onChange: (next: TrainingFilterValue) => void;
}) {
	const isDefault =
		value.query === NO_FILTERS.query &&
		value.edition === NO_FILTERS.edition &&
		value.template === NO_FILTERS.template;

	return (
		<div className="flex flex-wrap items-end gap-3 border-b border-line p-4">
			<label className="flex min-w-0 flex-1 basis-56 flex-col gap-1 text-xs font-bold text-muted">
				بحث
				<span className="relative">
					<Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
					<input
						type="search"
						value={value.query}
						onChange={(e) => onChange({ ...value, query: e.target.value })}
						placeholder="اسم التدريب أو رابطه…"
						className={`${FILTER_SELECT} w-full ps-9`}
					/>
				</span>
			</label>
			<label className="flex flex-col gap-1 text-xs font-bold text-muted">
				الطبعة الحالية
				<select
					value={value.edition}
					onChange={(e) =>
						onChange({
							...value,
							edition: e.target.value as TrainingFilterValue["edition"],
						})
					}
					className={FILTER_SELECT}
				>
					{EDITION_FILTERS.map((edition) => (
						<option key={edition} value={edition}>
							{edition === "all"
								? "كل الطبعات"
								: edition === "none"
									? "بلا طبعات"
									: EDITION_STATUS_LABELS[edition]}
						</option>
					))}
				</select>
			</label>
			<label className="flex flex-col gap-1 text-xs font-bold text-muted">
				القالب
				<select
					value={value.template}
					onChange={(e) =>
						onChange({
							...value,
							template: e.target.value as TrainingFilterValue["template"],
						})
					}
					className={FILTER_SELECT}
				>
					<option value="all">كل القوالب</option>
					{TEMPLATE_KEYS.map((key) => (
						<option key={key} value={key}>
							{TEMPLATE_LABELS[key]}
						</option>
					))}
				</select>
			</label>
			{isDefault ? null : (
				<button
					type="button"
					onClick={() => onChange(NO_FILTERS)}
					className="pb-2 text-xs font-bold text-teal-600 hover:underline"
				>
					إلغاء الفلاتر
				</button>
			)}
		</div>
	);
}

function TrainingsTable({
	trainings,
	editions,
	today,
	isLoading,
	canEdit,
	onEdit,
}: {
	trainings: TrainingSummary[];
	editions: EditionRow[];
	today: string;
	isLoading: boolean;
	canEdit: boolean;
	onEdit: (trainingId: string) => void;
}) {
	const { remove, isPending: removing } = useTrainingRemoval();
	const [filter, setFilter] = useState(NO_FILTERS);
	// Active trainings are the ones being sold, so they open first.
	const [tab, setTab] = useState<TrainingTab>("active");

	// Each training with its runs and where its current run stands, worked
	// out once for both the filter and the rows.
	const rows = useMemo(
		() =>
			trainings.map((training) => {
				const runs = editions.filter((e) => e.trainingId === training.id);
				const current = pickCurrentEdition(runs, today);
				return {
					training,
					runs,
					current,
					// The closest coming session across all of its runs.
					next: nextSessionOf(runs, today),
					currentStatus: current
						? editionStatus(
								isoDay(current.startDate),
								isoDay(current.endDate),
								today,
							)
						: null,
					confirmed: runs.reduce((sum, run) => sum + run._count.leads, 0),
				};
			}),
		[trainings, editions, today],
	);

	const query = normalize(filter.query);
	const matching = rows.filter(({ training, currentStatus }) => {
		if (
			query &&
			!normalize(training.name).includes(query) &&
			!training.slug.includes(query)
		) {
			return false;
		}
		if (filter.edition === "none" && currentStatus) return false;
		if (
			filter.edition !== "all" &&
			filter.edition !== "none" &&
			currentStatus !== filter.edition
		) {
			return false;
		}
		if (
			filter.template !== "all" &&
			asTemplateKey(training.template) !== filter.template
		) {
			return false;
		}
		return true;
	});
	const activeMatches = matching.filter((row) => row.training.isActive);
	const inactiveMatches = matching.filter((row) => !row.training.isActive);
	const tabTotal = rows.filter(
		(row) => row.training.isActive === (tab === "active"),
	).length;

	// Closest coming session first. Then trainings with nothing ahead, the one
	// that ran most recently first, then those with no edition at all.
	const visible = (tab === "active" ? activeMatches : inactiveMatches).sort(
		(a, b) => {
			if (a.next && b.next) {
				return (
					a.next.days - b.next.days ||
					a.training.name.localeCompare(b.training.name, "ar")
				);
			}
			if (a.next || b.next) return a.next ? -1 : 1;
			if (a.current && b.current) {
				return isoDay(b.current.endDate).localeCompare(
					isoDay(a.current.endDate),
				);
			}
			if (a.current || b.current) return a.current ? -1 : 1;
			return a.training.name.localeCompare(b.training.name, "ar");
		},
	);

	return (
		<div className="rounded-2xl border border-line bg-white shadow-card">
			<div className="px-4 pt-2">
				<TabBar
					tabs={[
						{ key: "active", label: "المفعّلة", count: activeMatches.length },
						{
							key: "inactive",
							label: "المعطّلة",
							count: inactiveMatches.length,
						},
					]}
					value={tab}
					onChange={setTab}
				/>
			</div>
			<TrainingFilters value={filter} onChange={setFilter} />
			{!isLoading && tabTotal ? (
				<p className="px-4 pt-3 text-xs font-semibold text-muted">
					{visible.length === tabTotal
						? `${tabTotal} تدريب`
						: `${visible.length} من ${tabTotal} تدريب`}
				</p>
			) : null}
			<div className="overflow-x-auto">
				<table className="w-full min-w-220 text-start text-sm">
					<thead>
						<tr className="border-b border-line bg-canvas text-muted">
							<th className="px-4 py-3 text-start font-bold">التدريب</th>
							<th className="px-4 py-3 text-start font-bold">الحالة</th>
							<th className="px-4 py-3 text-start font-bold">الطبعات</th>
							<th className="px-4 py-3 text-start font-bold">الطبعة الحالية</th>
							<th className="px-4 py-3 text-start font-bold">متبقٍ</th>
							<th className="px-4 py-3 text-start font-bold">
								الطلبات المؤكدة
							</th>
							<th className="px-4 py-3 text-start font-bold">إجراءات</th>
						</tr>
					</thead>
					<tbody>
						{visible.map(({ training, runs, current, next, confirmed }) => {
							return (
								<tr
									key={training.id}
									className="border-b border-line transition-colors last:border-0 hover:bg-canvas/60"
								>
									<td className="px-4 py-3">
										<Link
											to="/admin/trainings/$trainingId"
											params={{ trainingId: training.id }}
											className="font-semibold text-ink hover:text-teal-600 hover:underline"
										>
											{training.name}
										</Link>
										<span className="block text-xs text-muted">
											<bdi dir="ltr">/training/{training.slug}</bdi> ·{" "}
											{TEMPLATE_LABELS[asTemplateKey(training.template)]}
										</span>
									</td>
									<td className="px-4 py-3">
										<div className="flex flex-wrap gap-1">
											<span
												className={`rounded-md px-2 py-1 text-xs font-semibold ${
													training.isActive
														? "bg-teal-50 text-teal-600"
														: "bg-line/50 text-muted"
												}`}
											>
												{training.isActive ? "مفعّل" : "معطّل"}
											</span>
											{training.isDefault ? (
												<span className="rounded-md bg-gold-50 px-2 py-1 text-xs font-semibold text-gold-600">
													افتراضي
												</span>
											) : null}
										</div>
									</td>
									<td className="px-4 py-3 font-semibold text-navy">
										{runs.length}
									</td>
									<td className="px-4 py-3">
										{current ? (
											<div className="flex flex-wrap items-center gap-2">
												<Link
													to="/admin/editions/$editionId"
													params={{ editionId: String(current.courseNumber) }}
													className="font-semibold text-ink hover:text-teal-600 hover:underline"
												>
													{editionLabel(current.editionNumber)}
												</Link>
												<EditionStatusBadge edition={current} today={today} />
												<span className="text-xs text-muted">
													<bdi dir="ltr">
														{isoDay(current.startDate)} →{" "}
														{isoDay(current.endDate)}
													</bdi>
												</span>
											</div>
										) : (
											<span className="text-xs text-muted">
												لا توجد طبعات بعد
											</span>
										)}
									</td>
									<td className="px-4 py-3">
										{next ? (
											<div className="flex flex-col items-start gap-0.5">
												<span
													className={`whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold ${
														next.days <= SOON_DAYS
															? "bg-amber-50 text-amber-800"
															: "bg-teal-50 text-teal-600"
													}`}
												>
													{daysLeftLabel(next.days)}
												</span>
												{/* Say which run when it isn't the one beside it. */}
												{next.edition.courseNumber !== current?.courseNumber ? (
													<span className="text-xs text-muted">
														{editionLabel(next.edition.editionNumber)}
													</span>
												) : null}
											</div>
										) : (
											<span className="text-muted">—</span>
										)}
									</td>
									<td className="px-4 py-3">
										{confirmed ? (
											<Link
												to="/admin/leads"
												search={{ trainingId: training.id }}
												className="font-semibold text-navy hover:text-teal-600 hover:underline"
											>
												{confirmed}
											</Link>
										) : (
											<span className="text-muted">0</span>
										)}
									</td>
									<td className="px-4 py-3">
										<div className="flex flex-wrap items-center gap-1.5">
											<Link
												to="/admin/trainings/$trainingId"
												params={{ trainingId: training.id }}
												className={ROW_ACTION}
											>
												فتح
												<ChevronLeft className="h-3.5 w-3.5" />
											</Link>
											{canEdit ? (
												<button
													type="button"
													onClick={() => onEdit(training.id)}
													className={ROW_ACTION}
												>
													<Pencil className="h-3.5 w-3.5" />
													صفحة الهبوط
												</button>
											) : null}
											{canEdit ? (
												<button
													type="button"
													onClick={() =>
														remove(training, runs.length, confirmed)
													}
													disabled={removing}
													className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs font-bold text-red-600 transition-colors hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
												>
													<Trash2 className="h-3.5 w-3.5" />
													حذف
												</button>
											) : null}
										</div>
									</td>
								</tr>
							);
						})}

						{isLoading ? (
							<tr>
								<td colSpan={7} className="px-4 py-16 text-center">
									<Loader2 className="mx-auto h-5 w-5 animate-spin text-muted" />
								</td>
							</tr>
						) : null}
						{!isLoading && trainings.length === 0 ? (
							<tr>
								<td colSpan={7} className="px-4 py-12 text-center text-muted">
									لا توجد تدريبات بعد.
								</td>
							</tr>
						) : null}
						{!isLoading && trainings.length > 0 && tabTotal === 0 ? (
							<tr>
								<td colSpan={7} className="px-4 py-12 text-center text-muted">
									{tab === "active"
										? "لا توجد تدريبات مفعّلة."
										: "لا توجد تدريبات معطّلة."}
								</td>
							</tr>
						) : null}
						{!isLoading && tabTotal > 0 && visible.length === 0 ? (
							<tr>
								<td colSpan={7} className="px-4 py-12 text-center text-muted">
									لا توجد تدريبات تطابق الفلاتر.{" "}
									<button
										type="button"
										onClick={() => setFilter(NO_FILTERS)}
										className="font-bold text-teal-600 hover:underline"
									>
										إلغاء الفلاتر
									</button>
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
			</div>
		</div>
	);
}
